import { SourceType } from '@atlas/types'
import { WorkOrderComments } from '@/components/work-orders/WorkOrderComments'
import type { WorkOrder } from '@/domain/work-order.types'
import { useThumbnails } from '@/hooks/useThumbnails'
import { useDiscardSource, useUploadSource, useWorkOrderSources } from '@/hooks/useWorkOrderSources'
import { fileService } from '@/services/file.service'
import { useCommentThread } from './useCommentThread'

/**
 * A work order's comments on their own, without the audit log: the thread plus
 * its reference images. Every comment's images come from one read of the
 * order's reference files, so a thread with many images costs one discover.
 */
export function WorkOrderCommentThread({ workOrder: wo }: { workOrder: WorkOrder }) {
	const props = useCommentThread(wo.workOrderId)
	const references = useWorkOrderSources(wo.orderCode, SourceType.REFERENCE).data ?? []
	const thumbnails = useThumbnails(references)
	const upload = useUploadSource(wo.workOrderId, wo.orderCode, wo.tuple, SourceType.REFERENCE)
	const discard = useDiscardSource(wo.orderCode)
	const byId = new Map(references.map((file) => [file.fileId, file]))

	return (
		<WorkOrderComments
			{...props}
			onDeleteComment={(id) => {
				const attachments = props.comments.find((c) => c.id === id)?.attachments ?? []
				return props.onDeleteComment(id).then(() => {
					for (const { fileId } of attachments) discard(fileId)
				})
			}}
			onUploadImage={(file) => upload.mutateAsync({ file }).then((uploaded) => uploaded.fileId)}
			onDiscardImage={discard}
			imageOf={(fileId) => {
				const file = byId.get(fileId)
				return file ? { name: file.fileName, url: thumbnails[fileId] } : undefined
			}}
			fullSizeOf={fileService.getDownloadUrl}
		/>
	)
}
